import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Note: the bootstrap splash is intentionally NOT dismissed here. AuthContext
// calls window.__zaviBootHide() the moment the session check (/auth/me) resolves
// so the splash remains visible for the whole critical boot path — no premature
// flash, no artificial delay, no second loading state in between.

// Register the PWA service worker only in production builds — CRA dev server
// hot-reload conflicts with SW caching, so we skip it locally.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        // Auto-apply new versions the moment they finish installing so
        // installed PWAs never sit on a stale JS bundle across rapid
        // production redeploys. The UpdateBanner remains as a fallback
        // for edge cases where the auto-skip is blocked.
        const applyWaiting = () => {
          try { reg.waiting?.postMessage("SKIP_WAITING"); } catch {}
          window.dispatchEvent(new CustomEvent("zavi-sw-update-ready"));
        };
        if (reg.waiting) applyWaiting();
        reg.addEventListener("updatefound", () => {
          const next = reg.installing;
          if (!next) return;
          next.addEventListener("statechange", () => {
            if (next.state === "installed" && navigator.serviceWorker.controller) applyWaiting();
          });
        });
        // Poll for updates aggressively so long-lived PWA sessions
        // pick up fresh deploys within a minute of opening the app.
        setInterval(() => reg.update().catch(() => {}), 60 * 1000);
        // Also check when the tab regains focus / becomes visible
        window.addEventListener("focus", () => reg.update().catch(() => {}));
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") reg.update().catch(() => {});
        });
      })
      .catch((err) => console.warn("SW registration failed", err));

    // When a waiting SW takes control (after SKIP_WAITING), reload once.
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}
